import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Zap, Lock, Mail, Loader2 } from 'lucide-react';
import { useAuth } from '../lib/auth.js';

export function Login(): JSX.Element {
  const { login, canAccessRoute } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const loggedUser = await login(email.trim(), password);
      const landing = loggedUser?.landingRoute ?? '/meu-trabalho';
      // Tenta priorizar `from` do RouteGuard, MAS só se o user agora tem permissão.
      // Senão (ex: Patricia tentando `/`), cai no landingRoute do role dela.
      const fromState = (location.state as { from?: string } | null)?.from;
      const fromValid =
        fromState &&
        fromState !== '/' &&
        fromState !== '/login' &&
        (loggedUser?.allowedRoutes?.includes(fromState) ?? false);
      const target = fromValid ? (fromState as string) : landing;
      // Guard final: se mesmo o landing não passa no canAccessRoute, vai pra '/login' (loop quebra na proxima)
      navigate(canAccessRoute(target) ? target : landing, { replace: true });
    } catch (err) {
      setError(
        err instanceof Error && err.message === 'invalid_credentials'
          ? 'Email ou senha incorretos'
          : 'Falha ao entrar. Tente novamente.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
            <Zap className="h-6 w-6 text-primary" />
          </div>
          <div>
            <div className="font-bold tracking-tight text-lg">NextBoot CRM</div>
            <div className="text-[10px] text-muted-foreground">Shadow Ledger · v0.0.1</div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6 shadow-lg">
          <h1 className="text-lg font-semibold mb-1">Entrar</h1>
          <p className="text-xs text-muted-foreground mb-5">
            Acesse com sua conta da NextBoot.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground" htmlFor="email">
                Email
              </label>
              <div className="relative mt-1">
                <Mail className="h-4 w-4 text-muted-foreground absolute left-2.5 top-2.5" />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-10 rounded-md border border-border bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="patricia@nextboot.local"
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground" htmlFor="password">
                Senha
              </label>
              <div className="relative mt-1">
                <Lock className="h-4 w-4 text-muted-foreground absolute left-2.5 top-2.5" />
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-10 rounded-md border border-border bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>

            {error && (
              <div className="text-xs text-rose-400 border border-rose-500/30 bg-rose-500/10 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full h-10 rounded-md bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Entrando...
                </>
              ) : (
                'Entrar'
              )}
            </button>
          </form>
        </div>

        <div className="mt-4 text-[10px] text-center text-muted-foreground">
          Esqueceu a senha? Fale com o J. Lucas pra resetar.
        </div>
      </div>
    </div>
  );
}
